/**
 * The seam — merged roots for *sama asia* groups (specs/impact-path-v2-prd.md §4j).
 *
 * Two rules. Everything is stored: a member keeps every link. Every node is
 * drawn once, under one parent, and no connector crosses a column. So a
 * group of nodes joined by `is_same` is drawn as ONE merged root in the seam
 * — a fifth region above the walls — and the `is_same` links themselves are
 * never drawn as lines. The trace of the link on a wall is a mark at the
 * member's tip; the family light shows the whole thing on demand.
 *
 * Pure: pathways + connections in, the same pathways plus synthetic roots
 * out, with the lookups the renderer needs for marks and the light.
 */

export const SEAM_ID_PREFIX = 'seam:';

/** Same-as groups as connected components over is_same connections. */
function sameGroups(pathways, connections, { mutualOnly = false } = {}) {
  const ids = new Set(pathways.map((p) => p.id));
  const adj = new Map();
  for (const c of connections) {
    const t = c.type || c.connectionType || c.connection_type;
    if (t !== 'is_same') continue;
    if (mutualOnly && !((c.metadata?.confirmedBy || []).length >= 2)) continue;
    const a = c.parentId || c.parent_node_id; const b = c.childId || c.child_node_id;
    if (!ids.has(a) || !ids.has(b) || a === b) continue;
    (adj.get(a) || adj.set(a, new Set()).get(a)).add(b);
    (adj.get(b) || adj.set(b, new Set()).get(b)).add(a);
  }
  const seen = new Set(); const groups = [];
  for (const start of adj.keys()) {
    if (seen.has(start)) continue;
    const group = []; const stack = [start]; seen.add(start);
    while (stack.length) { const n = stack.pop(); group.push(n); for (const m of adj.get(n) || []) if (!seen.has(m)) { seen.add(m); stack.push(m); } }
    if (group.length >= 2) groups.push(group);
  }
  return groups;
}

/**
 * Build the seam. Returns:
 *   pathways   the input rows plus one synthetic root per group (type 'merged',
 *              no parent, members listed); members are left in place on their walls
 *   memberOf   memberId → rootId
 *   roots      rootId → { id, title, memberIds, scenarioIds, wallCount }
 *   isSame     the is_same connections, so the caller can keep them out of the arrows
 */
export function buildSeam(pathways = [], connections = [], { mutualOnly = false, scenarios = [] } = {}) {
  const groups = sameGroups(pathways, connections, { mutualOnly });
  if (!groups.length) return { pathways, memberOf: new Map(), roots: new Map(), isSame: [] };

  const byId = new Map(pathways.map((p) => [p.id, p]));
  const scenarioTitle = new Map(scenarios.map((s) => [s.id, s.title]));
  const memberOf = new Map(); const roots = new Map(); const synthetic = [];

  for (const group of groups) {
    const members = group.map((id) => byId.get(id)).filter(Boolean);
    // Title from the members (§4d rule 3): the shortest text is the title.
    const sorted = [...members].sort((a, b) => (a.text || a.title || '').length - (b.text || b.title || '').length);
    const title = sorted[0]?.text || sorted[0]?.title || 'Sama asia';
    const scenarioIds = [...new Set(members.map((m) => m.scenarioId || m.scenario_id).filter(Boolean))];
    // Valence (§4d rule 4): the members' if they agree, unknown if not.
    const vals = new Set(members.map((m) => m.valence).filter(Boolean));
    const valence = vals.size === 1 ? [...vals][0] : null;
    const id = `${SEAM_ID_PREFIX}${[...group].sort().join('+').slice(0, 200)}`;
    const votesUp = members.reduce((a, m) => a + (m.votesUp || m.votes_up || 0), 0);
    const root = {
      id, text: title, title, description: members.map((m) => m.text || m.title).filter(Boolean).join(' · '),
      type: 'merged', parentId: null, parent_id: null, depth: 0,
      scenarioId: null, scenarioIds, scenarioNames: scenarioIds.map((s) => scenarioTitle.get(s)).filter(Boolean),
      members: group, memberIds: group, wallCount: scenarioIds.length,
      valence, valence_source: valence ? 'derived' : null, votesUp, votes_up: votesUp,
      participantId: 'system', isScenarioStarter: false, is_scenario_starter: false,
      seam: true,
    };
    synthetic.push(root);
    roots.set(id, { id, title, memberIds: group, scenarioIds, wallCount: scenarioIds.length });
    for (const m of group) memberOf.set(m, id);
  }
  // Three-wall roots above two-wall ones, then by thumbs: the seam's own order.
  synthetic.sort((a, b) => b.wallCount - a.wallCount || b.votesUp - a.votesUp);
  const isSame = connections.filter((c) => (c.type || c.connectionType || c.connection_type) === 'is_same');
  return { pathways: [...synthetic, ...pathways], memberOf, roots, isSame };
}

/** The wall colours a mark shows: one dot per scenario the root joins. */
export function seamColours(root, scenarios = []) {
  const byId = new Map(scenarios.map((s) => [s.id, s.color || '#9ca3af']));
  return (root?.scenarioIds || []).map((id) => byId.get(id) || '#9ca3af');
}

/**
 * The family light (§4j): the root and its subtree, every member and its
 * chain up to its muuttuja. A Set of node ids to keep lit; everything else
 * dims. `pathways` here is the seamed list (roots included).
 */
export function familyOf(rootId, pathways = [], roots = new Map()) {
  const root = roots.get(rootId); if (!root) return null;
  const byId = new Map(pathways.map((p) => [p.id, p]));
  const children = new Map();
  for (const p of pathways) { const par = p.parentId || p.parent_id; if (par) (children.get(par) || children.set(par, []).get(par)).push(p.id); }
  const lit = new Set([rootId]);
  const down = (id) => { for (const c of children.get(id) || []) { if (!lit.has(c)) { lit.add(c); down(c); } } };
  down(rootId);
  for (const m of root.memberIds) {
    let cur = byId.get(m); let guard = 0;
    while (cur && guard++ < 40) { lit.add(cur.id); if (cur.isScenarioStarter || cur.is_scenario_starter) break; const par = cur.parentId || cur.parent_id; cur = par ? byId.get(par) : null; }
  }
  return lit;
}
